<script setup>
import {onMounted, ref} from "vue";
import Button from "../../components/forms/Button.vue";
import {getLoggedUser} from '@/services/AuthService';
import ClientService from "@/services/ClientService";
import IntermediaryPersonsService from "@/services/IntermediaryPersonsService";
import AccountManagersService from "@/services/AccountManagersService";
import {createConfirmDialog} from "vuejs-confirm-dialog";
import {formatDate, showSuccessMessage} from "@/helper";
import router from "@/router";
import {useDataTable} from "@/utils/datatableHelper";
import CreateUpdateClientModal from '@/components/modals/CreateUpdateClientModal.vue';

import {useI18n} from 'vue-i18n'
import BatchService from "@/services/BatchService";
import ModalDialog from "@/components/modals/ModalDialog.vue";
import IntermediaryService from "@/services/IntermediaryService";

const {t} = useI18n();

const datatableRef = ref(null);
const datatable = useDataTable(fetchBatches, 'created_at', 10, false, {}, 'BATCHES-INDEX', datatableRef);
const processingBatch = ref(null);
const processingBatchId = ref(null);

const columns = ref([
  {
    label: t('batchesIndex.created_at_datetime'),
    field: 'created_at',
    formatFn: (value) => formatDate(value, 'd.m.Y H:i')
  },
  {
    label: t('batchesIndex.state'),
    field: 'state',
    filterOptions: {
      enabled: true,
      skipDiacritics: true,
      placeholder: ' '
    },
    formatFn: (value) => {
      return t('batchesIndex.' + value);
    }
  },
  {
    label: t('batchesIndex.total_files_count'),
    field: 'total_files',
  },
  {
    label: t('batchesIndex.processed_files_count'),
    field: 'processed_files',
  },
  {
    label: '',
    field: 'action',
    sortable: false,
    html: true
  },
]);

const isProcessing = ref(false);

const isWaitingForBatchResponse = ref(true);

const batchInterval = ref(null);

onMounted(() => {
  isProcessingBatch();
});

function fetchBatches(body) {
  datatable.isLoading.value = true;
  BatchService.fetchRecords(body).then(response => {
    datatable.totalRecords.value = response.meta.total;
    datatable.rows.value = response.data;
    datatable.isLoading.value = false;
  });
}

function isProcessingBatch() {
  BatchService.getProcessingBatch().then(response => {
    if (response) {
      updateProcessingBatch(response);
      isProcessing.value = true;
    };
    isWaitingForBatchResponse.value = false;
  });
}

function updateProcessingBatch(data) {
  processingBatch.value = data;
  processingBatchId.value = data.id;
  datatable.fetchWithValues();
  batchInterval.value = setInterval(() => {
    getProcessingBatch(processingBatchId.value);
    datatable.fetchWithValues();
  }, 3000);
}

function uploadReports() {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(ModalDialog, {
    title: t('batchesIndex.uploading_reports'),
    message: t('batchesIndex.uploading_reports_confirm'),
  });
  onConfirm(() => {
    isProcessing.value = true;
    BatchService.uploadReports().then(response => {
      showSuccessMessage(response.message);
      updateProcessingBatch(response);
    });
  });
  reveal();
}

function getProcessingBatch(batchId) {
  BatchService.getBatch(batchId).then(response => {
    processingBatch.value = response;
    if (processingBatch.value.state === 'COMPLETED') {
      clearInterval(batchInterval.value);
      datatable.fetchWithValues();
      isProcessing.value = false;
      batchInterval.value = null;
      processingBatch.value = null;
    }
  });
}

function navigateTo(to) {
  router.push(to);
}

</script>

<template>
  <div class="page-section">
    <div class="page-section__heading">
      <div class="row">
        <div class="col-4">
          <a @click="router.back()" class="page-section__heading__back"><i class="mdi mdi-arrow-left"></i> {{ t('general.back') }}</a>
        </div>
        <div class="col-4 text-center">
          <div class="page-section__title">
            {{ t('batchesIndex.uploading_reports') }}
          </div>
        </div>
        <div class="col-4"></div>
      </div>
    </div>
  </div>
  <div class="page-section__content">
    <div class="row" style="margin-bottom: 100px;"> <!--replace style-->
      <div class="col-12  justify-content-center d-flex">
        <div class="card w-100">
          <div class="card-header">
            <div class="row">
              <div class="col text-center">
                <div class="card-header__title">
                  {{ t('batchesIndex.batch_processing') }}
                </div>
              </div>
            </div>
            <div v-if="!processingBatch" class="row mt-4 mb-4">
              <div v-if="!isWaitingForBatchResponse" class="col d-flex justify-content-center ">
                <Button :label="t('batchesIndex.start_upload')" size="lg" icon="mdi-text-box-plus" @click="uploadReports()" :disabled="isProcessing" :style="'display: flex; justify-content: center;'"></Button>
              </div>
              <div v-if="isWaitingForBatchResponse" class="text-center">
                  <div class="col-12 d-flex justify-content-center mt-2" v-for="i in 3" :key="i">
                    <Skeletor width="50%" height="50"/>
                  </div>
                </div>
            </div>
          </div>

          <div v-if="processingBatch" class="card-body">
            <div class="info-table">
              <div class="info-table__row row">
                <h4 class="fw-bold d-flex justify-content-center">{{ t('batchesIndex.processed') }} {{ processingBatch?.processed_files }}/{{ processingBatch?.total_files }}
                  <span class="spinner-border spinner-primary ms-3" role="status">
                  <span class="sr-only">Loading...</span>
                </span>
                </h4>
              </div>
              <div class="info-table__row row">
                <div class="col mt-3">
                  <div class="progress" style="height: 20px; margin-left: 10px; margin-right: 10px;">
                    <div class="progress-bar text-white" role="progressbar" :style="{ width: (processingBatch?.percents_done ?? 0) + '%' }" :aria-valuenow="processingBatch?.percents_done" aria-valuemin="0" aria-valuemax="100">{{ processingBatch?.percents_done }} %</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="mt-3">
      <vue-good-table
          ref="datatableRef"
          :columns="columns"
          :rows="datatable.rows.value"
          mode="remote"
          @page-change="datatable.onPageChange"
          @sort-change="datatable.onSortChange"
          @column-filter="datatable.onColumnFilter"
          @per-page-change="datatable.onPerPageChange"
          :totalRows="datatable.totalRecords.value"
          :pagination-options="{
            enabled: true,
            rowsPerPageLabel: t('general.entriesPerPage'),
            nextLabel: t('general.next'),
            prevLabel: t('general.previous'),
            ofLabel: t('general.from'),
          }">
        <template #table-row="props">
                            <span v-if="props.column.field == 'action'">
                                <span v-if="props.row.state === 'IN_PROCESS'" class="spinner-border spinner-border-sm spinner-primary" role="status">
                                  <span class="sr-only">Loading...</span>
                                </span>
                                <span style="font-weight: bold; float: right">
                                    <router-link :to="{ name: 'BatchDetail', params: { id: props.row.id} }"><button type="button" class="btn btn-link"><i class="mdi mdi-eye"/></button></router-link>
                                </span>
                            </span>
        </template>
        <template v-slot:emptystate v-if="!datatable.isLoading.value">
          <div class="text-center">
            {{ t('general.no_records_found') }}
          </div>
        </template>
        <template v-slot:emptystate v-if="datatable.isLoading.value">
          <div class="text-center">
            {{ t('general.loading') }}
          </div>
        </template>
      </vue-good-table>
    </div>
  </div>
</template>

<style scoped>

</style>
